// Browser caching utilities for API responses and data

interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

interface CacheConfig {
  defaultTTL: number;
  maxSize: number;
  storageType: 'memory' | 'localStorage' | 'sessionStorage';
}

export class BrowserCache {
  private memoryCache = new Map<string, CacheItem<unknown>>();
  private config: CacheConfig;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = {
      defaultTTL: 5 * 60 * 1000, // 5 minutes default
      maxSize: 100,
      storageType: 'memory',
      ...config,
    };
  }

  // Set item in cache
  set<T>(key: string, data: T, ttl?: number): void {
    const item: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.config.defaultTTL,
    };

    switch (this.config.storageType) {
      case 'memory':
        this.setMemoryCache(key, item);
        break;
      case 'localStorage':
        this.setStorageCache(key, item, localStorage);
        break;
      case 'sessionStorage':
        this.setStorageCache(key, item, sessionStorage);
        break;
    }
  }

  // Get item from cache
  get<T>(key: string): T | null {
    let item: CacheItem<T> | null = null;

    switch (this.config.storageType) {
      case 'memory':
        item = this.getMemoryCache(key);
        break;
      case 'localStorage':
        item = this.getStorageCache(key, localStorage);
        break;
      case 'sessionStorage':
        item = this.getStorageCache(key, sessionStorage);
        break;
    }

    if (!item) {
      return null;
    }

    // Check if item has expired
    if (Date.now() - item.timestamp > item.ttl) {
      this.delete(key);
      return null;
    }

    return item.data;
  }

  // Delete item from cache
  delete(key: string): void {
    switch (this.config.storageType) {
      case 'memory':
        this.memoryCache.delete(key);
        break;
      case 'localStorage':
        localStorage.removeItem(this.getCacheKey(key));
        break;
      case 'sessionStorage':
        sessionStorage.removeItem(this.getCacheKey(key));
        break;
    }
  }

  // Clear all cache
  clear(): void {
    switch (this.config.storageType) {
      case 'memory':
        this.memoryCache.clear();
        break;
      case 'localStorage':
        this.clearStorageCache(localStorage);
        break;
      case 'sessionStorage':
        this.clearStorageCache(sessionStorage);
        break;
    }
  }

  // Check if item exists and is valid
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  // Get cache statistics
  getStats() {
    const size =
      this.config.storageType === 'memory'
        ? this.memoryCache.size
        : this.getStorageSize();

    return {
      size,
      maxSize: this.config.maxSize,
      storageType: this.config.storageType,
    };
  }

  private setMemoryCache<T>(key: string, item: CacheItem<T>): void {
    // Implement LRU eviction if cache is full
    if (this.memoryCache.size >= this.config.maxSize) {
      const firstKey = this.memoryCache.keys().next().value;
      if (firstKey) {
        this.memoryCache.delete(firstKey);
      }
    }
    this.memoryCache.set(key, item);
  }

  private getMemoryCache<T>(key: string): CacheItem<T> | null {
    const item = this.memoryCache.get(key);
    return item ? (item as CacheItem<T>) : null;
  }

  private setStorageCache<T>(
    key: string,
    item: CacheItem<T>,
    storage: Storage
  ): void {
    try {
      storage.setItem(this.getCacheKey(key), JSON.stringify(item));
    } catch (error) {
      console.warn('Failed to set cache item in storage:', error);
      // Storage might be full, try to clear some space
      this.clearExpiredItems(storage);
      try {
        storage.setItem(this.getCacheKey(key), JSON.stringify(item));
      } catch (retryError) {
        console.error('Failed to set cache item after cleanup:', retryError);
      }
    }
  }

  private getStorageCache<T>(
    key: string,
    storage: Storage
  ): CacheItem<T> | null {
    try {
      const item = storage.getItem(this.getCacheKey(key));
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.warn('Failed to get cache item from storage:', error);
      return null;
    }
  }

  private clearStorageCache(storage: Storage): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith('lumora-cache-')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => storage.removeItem(key));
  }

  private clearExpiredItems(storage: Storage): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith('lumora-cache-')) {
        try {
          const item = JSON.parse(storage.getItem(key) || '');
          if (Date.now() - item.timestamp > item.ttl) {
            keysToRemove.push(key);
          }
        } catch {
          // Invalid item, remove it
          keysToRemove.push(key);
        }
      }
    }
    keysToRemove.forEach(key => storage.removeItem(key));
  }

  private getStorageSize(): number {
    let count = 0;
    const storage =
      this.config.storageType === 'localStorage'
        ? localStorage
        : sessionStorage;

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith('lumora-cache-')) {
        count++;
      }
    }
    return count;
  }

  private getCacheKey(key: string): string {
    return `lumora-cache-${key}`;
  }
}

// Cache instances for different use cases
export const apiCache = new BrowserCache({
  defaultTTL: 5 * 60 * 1000, // 5 minutes for API responses
  maxSize: 50,
  storageType: 'memory',
});

export const portfolioCache = new BrowserCache({
  defaultTTL: 2 * 60 * 1000, // 2 minutes for portfolio data
  maxSize: 20,
  storageType: 'sessionStorage',
});

export const userPreferencesCache = new BrowserCache({
  defaultTTL: 24 * 60 * 60 * 1000, // 24 hours for user preferences
  maxSize: 10,
  storageType: 'localStorage',
});

// Cache TTL constants
export const CACHE_TTL = {
  SHORT: 1 * 60 * 1000, // 1 minute
  MEDIUM: 5 * 60 * 1000, // 5 minutes
  LONG: 30 * 60 * 1000, // 30 minutes
  VERY_LONG: 24 * 60 * 60 * 1000, // 24 hours
} as const;

// Utility functions for common caching patterns
export function cacheApiResponse<T>(
  key: string,
  data: T,
  ttl: number = CACHE_TTL.MEDIUM
): void {
  apiCache.set(key, data, ttl);
}

export function getCachedApiResponse<T>(key: string): T | null {
  return apiCache.get<T>(key);
}

export function invalidateApiCache(pattern?: string): void {
  if (pattern) {
    // This would require implementing pattern matching
    // For now, just clear all
    apiCache.clear();
  } else {
    apiCache.clear();
  }
}

// HTTP cache headers utility
export function getCacheHeaders(maxAge: number = 300): HeadersInit {
  return {
    'Cache-Control': `public, max-age=${maxAge}`,
    ETag: `"${Date.now()}"`,
    'Last-Modified': new Date().toUTCString(),
  };
}

// Preload critical resources
export function preloadCriticalResources(): void {
  // Note: API endpoints are not implemented - app uses direct Supabase calls
  // Preloading non-existent endpoints causes 404 errors
  // Consider preloading actual static assets if needed
}

// Resource hints for performance
export function addResourceHints(): void {
  // DNS prefetch for external domains
  const domains = ['fonts.googleapis.com', 'fonts.gstatic.com'];

  domains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = `//${domain}`;
    document.head.appendChild(link);
  });

  // Preconnect to critical origins
  const criticalOrigins = [process.env.VITE_SUPABASE_URL].filter(Boolean);

  criticalOrigins.forEach(origin => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin!;
    document.head.appendChild(link);
  });
}
